import { create } from 'zustand';
import {
  flushEngineSettings,
  loadEngineSettings,
  saveEngineSettings,
  type EngineSettings,
} from './engineRuntimeStorePersistence';

// ── Defaults (engine runtime + display) ───────────────────────────────────────
const DEFAULT_MASTER_VOLUME = 0.9;
export const DEFAULT_BLOOM_INTENSITY = 0.24;
export const DEFAULT_VIGNETTE_AMOUNT = 0.42;
const DEFAULT_GRAIN_OPACITY = 0.028;
const DEFAULT_CHROMATIC_ABERRATION = true;

export const DEFAULT_MOUSE_SENSITIVITY = 0.5;
export const DEFAULT_CAMERA_MODE_PREF = 'first' as const;
export const DEFAULT_INVERT_Y = false;
export const DEFAULT_FOV = 60;
export const DEFAULT_HEAD_BOB = true;
export const DEFAULT_MOVE_SPEED_MULTIPLIER = 1.0; // 0.6-1.2

// ── Input bindings ─────────────────────────────────────────────────────────────
export type ActionId =
  | 'moveForward'
  | 'moveBack'
  | 'moveLeft'
  | 'moveRight'
  | 'primaryAction'
  | 'interact'
  | 'toggleCamera';

export interface KeyBindings {
  moveForward: string;
  moveBack: string;
  moveLeft: string;
  moveRight: string;
  /** Game-defined primary action (light toggle, fire, use, etc.). */
  primaryAction: string;
  interact: string;
  toggleCamera: string;
}

export const DEFAULT_KEY_BINDINGS: KeyBindings = {
  moveForward: 'KeyW',
  moveBack: 'KeyS',
  moveLeft: 'KeyA',
  moveRight: 'KeyD',
  primaryAction: 'KeyF',
  interact: 'KeyE',
  toggleCamera: 'KeyV',
};

function clamp01(value: number) {
  return Math.min(1, Math.max(0, value));
}

/** A one-shot position override for developer tooling (e.g. a game's dev
 *  console `tp` command) - not the in-editor "Playtest" respawn, which has
 *  its own reactive flow on `useMapEditorStore`. */
export interface DevTeleportRequest {
  x: number;
  z: number;
  floor: number;
}

/**
 * Engine runtime + tool state, owned by the engine (not any game). Holds player
 * settings the controller/camera/post-FX read, the master audio level, and the
 * developer-tool flags the engine consumes (console open, fly mode, full-bright).
 * Game-specific state (battery, inventory, antagonists, per-bus audio mix, etc.)
 * lives in the game's own store.
 */
export interface EngineRuntimeState {
  /** Active camera mode this frame. */
  cameraMode: 'first' | 'third';
  /** Current floor index. Games map this onto their own floor semantics. */
  currentFloor: number;

  // ── Player settings ──────────────────────────────────────────────────────
  mouseSensitivity: number;
  cameraModePref: 'first' | 'third';
  /**
   * A creator's Game Setup camera choice, when set: forces the mode for
   * players (FirstPersonController ignores the live toggle key while this is
   * non-null) rather than just overriding `cameraModePref`'s default. Per-
   * session like `cameraMode`/`currentFloor`, not persisted: it reflects
   * whichever project is currently loaded, not a remembered player setting.
   */
  cameraModeForced: 'first' | 'third' | null;
  invertY: boolean;
  fov: number;
  headBob: boolean;
  moveSpeedMultiplier: number;
  keyBindings: KeyBindings;

  // ── Audio (master mix only; per-bus mixes belong to the game) ─────────────
  masterVolume: number;

  // ── Post-processing ───────────────────────────────────────────────────────
  bloomIntensity: number;
  vignetteAmount: number;
  grainOpacity: number;
  chromaticAberration: boolean;

  /** True while the game is paused (e.g. Escape mid-play). Consumers freeze
   *  simulation (Canvas frameloop) and release pointer lock while this is set. */
  paused: boolean;

  // ── Developer tools ─────────────────────────────────────────────────────────
  devConsoleOpen: boolean;
  devFlyMode: boolean;
  /** When true, the scene is lit to full daytime brightness. */
  devFullBright: boolean;
  /** Non-null for exactly one render: FirstPersonController applies it and
   *  clears it back to null the same tick. */
  devTeleportRequest: DevTeleportRequest | null;

  setPaused: (paused: boolean) => void;
  setCameraMode: (mode: 'first' | 'third') => void;
  setCurrentFloor: (floor: number) => void;
  setMouseSensitivity: (value: number) => void;
  setCameraModePref: (mode: 'first' | 'third') => void;
  setCameraModeForced: (mode: 'first' | 'third' | null) => void;
  setInvertY: (enabled: boolean) => void;
  setFov: (value: number) => void;
  setHeadBob: (enabled: boolean) => void;
  setMoveSpeedMultiplier: (value: number) => void;
  setKeyBinding: (action: ActionId, code: string) => void;
  resetKeyBindings: () => void;
  setMasterVolume: (value: number) => void;
  setBloomIntensity: (value: number) => void;
  setVignetteAmount: (value: number) => void;
  setGrainOpacity: (value: number) => void;
  setChromaticAberration: (enabled: boolean) => void;
  setDevConsoleOpen: (open: boolean) => void;
  toggleDevConsole: () => void;
  setDevFlyMode: (enabled: boolean) => void;
  setDevFullBright: (enabled: boolean) => void;
  requestDevTeleport: (request: DevTeleportRequest) => void;
  clearDevTeleportRequest: () => void;
}

// Settings persisted across sessions (localStorage) - loaded once at module
// init so the very first render already reflects what the player last set,
// with no flash of defaults. See engineRuntimeStorePersistence.ts. Only the
// player-settings fields are persisted; cameraMode/currentFloor are per-session
// runtime state and devConsoleOpen/devFlyMode/devFullBright are dev-only tools.
const persistedSettings = loadEngineSettings({
  mouseSensitivity: DEFAULT_MOUSE_SENSITIVITY,
  cameraModePref: DEFAULT_CAMERA_MODE_PREF,
  invertY: DEFAULT_INVERT_Y,
  fov: DEFAULT_FOV,
  headBob: DEFAULT_HEAD_BOB,
  moveSpeedMultiplier: DEFAULT_MOVE_SPEED_MULTIPLIER,
  keyBindings: { ...DEFAULT_KEY_BINDINGS },
  masterVolume: DEFAULT_MASTER_VOLUME,
  bloomIntensity: DEFAULT_BLOOM_INTENSITY,
  vignetteAmount: DEFAULT_VIGNETTE_AMOUNT,
  grainOpacity: DEFAULT_GRAIN_OPACITY,
  chromaticAberration: DEFAULT_CHROMATIC_ABERRATION,
});

/** Pulls just the persisted-settings slice out of full engine runtime state. */
function extractSettings(state: EngineRuntimeState): EngineSettings {
  return {
    mouseSensitivity: state.mouseSensitivity,
    cameraModePref: state.cameraModePref,
    invertY: state.invertY,
    fov: state.fov,
    headBob: state.headBob,
    moveSpeedMultiplier: state.moveSpeedMultiplier,
    keyBindings: state.keyBindings,
    masterVolume: state.masterVolume,
    bloomIntensity: state.bloomIntensity,
    vignetteAmount: state.vignetteAmount,
    grainOpacity: state.grainOpacity,
    chromaticAberration: state.chromaticAberration,
  };
}

export const useEngineRuntimeStore = create<EngineRuntimeState>((set, get) => ({
  cameraMode: 'first',
  currentFloor: 0,
  mouseSensitivity: persistedSettings.mouseSensitivity,
  cameraModePref: persistedSettings.cameraModePref,
  cameraModeForced: null,
  invertY: persistedSettings.invertY,
  fov: persistedSettings.fov,
  headBob: persistedSettings.headBob,
  moveSpeedMultiplier: persistedSettings.moveSpeedMultiplier,
  keyBindings: { ...persistedSettings.keyBindings },
  masterVolume: persistedSettings.masterVolume,
  bloomIntensity: persistedSettings.bloomIntensity,
  vignetteAmount: persistedSettings.vignetteAmount,
  grainOpacity: persistedSettings.grainOpacity,
  chromaticAberration: persistedSettings.chromaticAberration,
  paused: false,
  devConsoleOpen: false,
  devFlyMode: false,
  devFullBright: false,
  devTeleportRequest: null,

  setPaused: (paused) => set({ paused }),
  setCameraMode: (mode) => set({ cameraMode: mode }),
  setCurrentFloor: (floor) => set({ currentFloor: floor }),
  // Every numeric setter below rejects a non-finite value (NaN/Infinity, e.g.
  // from a settings input mid-edit via parseFloat) by keeping the current
  // value instead, matching engineRuntimeStorePersistence.ts's own
  // isFiniteNumber guard on load - the clamps here would otherwise pass NaN
  // straight through (Math.max(0, NaN) is NaN) and persist it for the rest
  // of the session.
  setMouseSensitivity: (value) => set({ mouseSensitivity: Number.isFinite(value) ? clamp01(value) : get().mouseSensitivity }),
  setCameraModePref: (mode) => set({ cameraModePref: mode }),
  setCameraModeForced: (mode) => set({ cameraModeForced: mode }),
  setInvertY: (enabled) => set({ invertY: enabled }),
  setFov: (value) => set({ fov: Number.isFinite(value) ? Math.min(110, Math.max(40, value)) : get().fov }),
  setHeadBob: (enabled) => set({ headBob: enabled }),
  setMoveSpeedMultiplier: (value) =>
    set({ moveSpeedMultiplier: Number.isFinite(value) ? Math.min(1.2, Math.max(0.6, value)) : get().moveSpeedMultiplier }),
  setKeyBinding: (action, code) =>
    set((state) => ({ keyBindings: { ...state.keyBindings, [action]: code } })),
  resetKeyBindings: () => set({ keyBindings: { ...DEFAULT_KEY_BINDINGS } }),
  setMasterVolume: (value) => set({ masterVolume: Number.isFinite(value) ? clamp01(value) : get().masterVolume }),
  setBloomIntensity: (value) => set({ bloomIntensity: Number.isFinite(value) ? clamp01(value) : get().bloomIntensity }),
  setVignetteAmount: (value) => set({ vignetteAmount: Number.isFinite(value) ? clamp01(value) : get().vignetteAmount }),
  setGrainOpacity: (value) => set({ grainOpacity: Number.isFinite(value) ? clamp01(value) : get().grainOpacity }),
  setChromaticAberration: (enabled) => set({ chromaticAberration: enabled }),
  setDevConsoleOpen: (open) => set({ devConsoleOpen: open }),
  toggleDevConsole: () => set((state) => ({ devConsoleOpen: !state.devConsoleOpen })),
  setDevFlyMode: (enabled) => set({ devFlyMode: enabled }),
  setDevFullBright: (enabled) => set({ devFullBright: enabled }),
  requestDevTeleport: (request) => set({ devTeleportRequest: request }),
  clearDevTeleportRequest: () => set({ devTeleportRequest: null }),
}));

// Persist settings whenever they actually change. cameraMode/currentFloor
// change during ordinary play, so this diffs only the settings slice before
// scheduling a (debounced) write - otherwise unrelated runtime updates would
// keep re-triggering the debounce and it would never flush.
useEngineRuntimeStore.subscribe((state, prevState) => {
  const next = extractSettings(state);
  const prev = extractSettings(prevState);
  const changed = (Object.keys(next) as (keyof EngineSettings)[]).some((key) => next[key] !== prev[key]);
  if (changed) saveEngineSettings(next);
});

if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => {
    flushEngineSettings(extractSettings(useEngineRuntimeStore.getState()));
  });
}
